/**
 * One serialiser for every backend error payload.
 *
 * FastAPI puts the payload in `detail`, and `detail` is not always a string:
 *
 *   {"detail": "Audience not found"}                                          a plain HTTPException
 *   {"detail": {"error": "...", "upgrade_required": true}}                    a quota refusal
 *   {"detail": {"code": "ACCOUNT_DISABLED", "message": "..."}}                a structured auth refusal
 *   {"detail": [{"loc": [...], "msg": "field required"}]}                     a 422
 *   {"error": "..."}                                                          the legacy Flask routes
 *
 * Call sites wrote `data.error || data.detail || 'fallback'`. For the object shapes that expression YIELDS
 * THE OBJECT, and `new Error(obj)` or a template literal then renders it as the string "[object Object]" —
 * which is what a summit attendee saw instead of "you have used all five simulations". The same shape reached
 * simulation chat as `Failed to send message: [object Object]`.
 *
 * This is deliberately ONE function rather than a per-surface fix. Four separate ad-hoc versions had already
 * grown in the tree (`describeDetail` in rlhfPairwise, `toMessage` in MarketplaceGate, `detailMessage` in
 * AuthContext, an inline ternary in Login), each covering a different subset of the shapes, and the payload
 * that broke here was in none of their subsets.
 */

/** Shown when the payload carries no sentence at all. Never blank, never a raw shape. */
export const GENERIC_API_ERROR = 'Something went wrong. Please try again.';

/**
 * The keys a sentence can hide behind, in priority order. `error` first because the older routes send both
 * (`{"error": "...", "detail": "..."}`) and the `error` is the one written for a human.
 */
const MESSAGE_KEYS = ['error', 'detail', 'message', 'msg'] as const;

/** Payloads nest at most a few levels; the bound is what makes a cyclic object safe rather than fatal. */
const MAX_DEPTH = 6;

const describe = (value: unknown, depth: number): string => {
  if (depth > MAX_DEPTH || value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  // A bare number or boolean is a code or a flag, not something to show a user. Returning String(value)
  // here would put "0" or "false" in a red banner.
  if (typeof value !== 'object') return '';
  if (value instanceof Error) return describe(value.message, depth + 1);
  if (Array.isArray(value)) {
    // A 422 is a LIST of problems and all of them matter — reporting only the first tells someone to fix one
    // field and resubmit into the same wall.
    return value
      .map((item) => describe(item, depth + 1))
      .filter(Boolean)
      .join('; ');
  }

  const record = value as Record<string, unknown>;
  for (const key of MESSAGE_KEYS) {
    if (!(key in record)) continue;
    const described = describe(record[key], depth + 1);
    if (described) return described;
  }
  // Anything else is a shape with no sentence in it. Falling through to '' (and so to the caller's fallback)
  // is the whole point: there is no branch here that can stringify an object.
  return '';
};

/**
 * Turn any error payload — a parsed JSON body, an `Error`, a string, `null` — into one sentence.
 *
 * Pass a `fallback` when the call site knows what failed ("Failed to start simulation"); pass `''` when the
 * caller means to append its own detail, such as an HTTP status code.
 */
export const apiErrorMessage = (payload: unknown, fallback: string = GENERIC_API_ERROR): string =>
  describe(payload, 0) || fallback;

/**
 * Did the server refuse this because a plan limit is spent?
 *
 * Checked at the top level AND inside `detail`. The FastAPI routes raise
 * `HTTPException(403, detail={'error': ..., 'upgrade_required': True})`, so the flag is NESTED; the frontend
 * only ever looked at the top level, `UsageLimitExceededError` was therefore never thrown, and nothing
 * downstream could tell a spent quota from a network blip. That is why the Run button stayed enabled after
 * the sixth simulation was refused.
 */
export const isUpgradeRequired = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object' || payload instanceof Error) return false;
  const record = payload as Record<string, unknown>;
  if (record.upgrade_required === true) return true;
  const detail = record.detail;
  return (
    !!detail &&
    typeof detail === 'object' &&
    (detail as Record<string, unknown>).upgrade_required === true
  );
};
